/**
 * Formats change-request old/new/current values for admin review.
 * Handles bilingual JSON objects and hides .NET type-name ToString() junk.
 */

import type { TFunction } from 'i18next';
import { StudyDegreeType } from '../types/member';
import { tryNormalizeEnumValue } from './enumNormalize';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function formatBilingualText(obj: Record<string, unknown>): string | null {
  const en = typeof obj.en === 'string' ? obj.en.trim() : '';
  const gr = typeof obj.gr === 'string' ? obj.gr.trim() : '';
  if (!en && !gr) return null;
  if (en && gr && en !== gr) return `${gr} / ${en}`;
  return gr || en;
}

function formatBilingualName(obj: Record<string, unknown>): string | null {
  const firstEn = typeof obj.firstNameEn === 'string' ? obj.firstNameEn.trim() : '';
  const firstGr = typeof obj.firstNameGr === 'string' ? obj.firstNameGr.trim() : '';
  const lastEn = typeof obj.lastNameEn === 'string' ? obj.lastNameEn.trim() : '';
  const lastGr = typeof obj.lastNameGr === 'string' ? obj.lastNameGr.trim() : '';
  const first = [firstGr, firstEn].filter(Boolean);
  const last = [lastGr, lastEn].filter(Boolean);
  const firstPart =
    first.length > 1 && first[0] !== first[1] ? first.join(' / ') : first[0] || '';
  const lastPart =
    last.length > 1 && last[0] !== last[1] ? last.join(' / ') : last[0] || '';
  const full = [firstPart, lastPart].filter(Boolean).join(' ').trim();
  return full || null;
}

function formatBilingualAddress(obj: Record<string, unknown>): string | null {
  const pick = (enKey: string, grKey: string) => {
    const en = typeof obj[enKey] === 'string' ? (obj[enKey] as string).trim() : '';
    const gr = typeof obj[grKey] === 'string' ? (obj[grKey] as string).trim() : '';
    if (en && gr && en !== gr) return `${gr} / ${en}`;
    return gr || en || '';
  };
  const parts = [
    pick('addressEn', 'addressGr'),
    pick('postalCodeEn', 'postalCodeGr'),
    pick('cityEn', 'cityGr'),
    pick('countryEn', 'countryGr'),
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

function degreeTypeLabel(value: unknown, t?: TFunction): string {
  const normalized = tryNormalizeEnumValue(StudyDegreeType, value);
  if (normalized === StudyDegreeType.Bachelors) return t ? t('register.studies.bachelors') : "Bachelor's";
  if (normalized === StudyDegreeType.Masters) return t ? t('register.studies.masters') : "Master's";
  if (normalized === StudyDegreeType.PhD) return t ? t('register.studies.phd') : 'PhD';
  if (normalized === StudyDegreeType.Other) return t ? t('register.studies.other') : 'Other';
  return value != null ? String(value) : '';
}

function nestedFieldLabel(key: string, t?: TFunction): string {
  const keys: Record<string, string> = {
    degreeType: 'register.studies.degreeType',
    DegreeType: 'register.studies.degreeType',
    institutionEn: 'register.studies.institutionEn',
    InstitutionEn: 'register.studies.institutionEn',
    institutionGr: 'register.studies.institutionGr',
    InstitutionGr: 'register.studies.institutionGr',
    startYear: 'register.studies.startYear',
    StartYear: 'register.studies.startYear',
    completionYear: 'register.studies.completionYear',
    CompletionYear: 'register.studies.completionYear',
    duration: 'register.studies.duration',
    titleEn: 'register.studies.studyTitleEn',
    titleGr: 'register.studies.studyTitleGr',
    institution: 'register.studies.institution',
  };
  const i18nKey = keys[key];
  if (i18nKey && t) {
    const translated = t(i18nKey);
    if (translated && translated !== i18nKey) return translated;
  }
  return key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ');
}

function formatStudyRecord(obj: Record<string, unknown>, t?: TFunction): string | null {
  const institution =
    formatBilingualText({
      en: typeof obj.institutionEn === 'string' ? obj.institutionEn : '',
      gr: typeof obj.institutionGr === 'string' ? obj.institutionGr : '',
    }) ||
    (typeof obj.institution === 'string' ? obj.institution.trim() : '') ||
    '';
  const title =
    formatBilingualText({
      en: typeof obj.titleEn === 'string' ? obj.titleEn : '',
      gr: typeof obj.titleGr === 'string' ? obj.titleGr : '',
    }) || '';
  const degree = degreeTypeLabel(obj.degreeType ?? obj.DegreeType, t);
  const start = obj.startYear != null ? String(obj.startYear) : obj.StartYear != null ? String(obj.StartYear) : '';
  const presentLabel = t ? t('register.studies.isCurrent') : 'present';
  const end = obj.isCurrent
    ? presentLabel
    : obj.completionYear != null
      ? String(obj.completionYear)
      : obj.CompletionYear != null
        ? String(obj.CompletionYear)
        : '';
  const years = start && end ? `${start}–${end}` : start || end;
  const parts = [institution, title, degree, years].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

const GUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function looksLikeGuid(value: unknown): boolean {
  return typeof value === 'string' && GUID_RE.test(value.trim());
}

function formatParsedObject(obj: Record<string, unknown>, t?: TFunction): string | null {
  // Strip EF/JSON computed flags and internal IDs when present alongside real fields.
  const cleaned = { ...obj };
  delete cleaned.hasValue;
  delete cleaned.isEmpty;
  delete cleaned.hasFirstName;
  delete cleaned.hasLastName;
  delete cleaned.id;
  delete cleaned.Id;

  if ('firstNameEn' in cleaned || 'firstNameGr' in cleaned || 'lastNameEn' in cleaned || 'lastNameGr' in cleaned) {
    return formatBilingualName(cleaned);
  }
  if ('addressEn' in cleaned || 'addressGr' in cleaned || 'cityEn' in cleaned || 'cityGr' in cleaned) {
    return formatBilingualAddress(cleaned);
  }
  if (
    'institution' in cleaned ||
    'institutionEn' in cleaned ||
    'startYear' in cleaned ||
    'completionYear' in cleaned
  ) {
    return formatStudyRecord(cleaned, t);
  }
  if ('en' in cleaned || 'gr' in cleaned) {
    return formatBilingualText(cleaned);
  }

  const entries = Object.entries(cleaned).filter(
    ([, v]) => v != null && v !== '' && typeof v !== 'boolean' && !looksLikeGuid(v)
  );
  if (!entries.length) return null;
  return entries
    .map(([k, v]) => {
      const label = nestedFieldLabel(k, t);
      if (isPlainObject(v)) {
        return `${label}: ${formatParsedObject(v, t) || '—'}`;
      }
      if (k === 'degreeType' || k === 'DegreeType') {
        return `${label}: ${degreeTypeLabel(v, t)}`;
      }
      return `${label}: ${String(v)}`;
    })
    .join('; ');
}

/** True when a value looks like a .NET type name dumped via Object.ToString(). */
function isDotNetTypeName(value: string): boolean {
  return /^ICOM\.API\.Models\./.test(value) || /^System\./.test(value);
}

export function formatChangeRequestValue(raw?: string | null, t?: TFunction): string {
  if (raw == null || raw === '') return '—';
  const trimmed = raw.trim();
  if (isDotNetTypeName(trimmed)) return '—';

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (typeof parsed === 'string') return parsed || '—';
    if (typeof parsed === 'number' || typeof parsed === 'boolean') return String(parsed);
    if (Array.isArray(parsed)) {
      if (!parsed.length) return '—';
      // Committee ID lists: show a count instead of raw GUIDs.
      if (parsed.every(looksLikeGuid)) {
        return parsed.length === 1
          ? '1 selected committee'
          : `${parsed.length} selected committees`;
      }
      return parsed
        .map(item => {
          if (looksLikeGuid(item)) return null;
          if (isPlainObject(item)) return formatParsedObject(item, t);
          return String(item);
        })
        .filter(Boolean)
        .join(', ') || '—';
    }
    if (isPlainObject(parsed)) {
      return formatParsedObject(parsed, t) || '—';
    }
  } catch {
    // not JSON — fall through
  }

  if (looksLikeGuid(trimmed)) {
    return '—';
  }

  return trimmed;
}
